import { useState, useEffect, useCallback, useMemo } from 'react';
import type { DTableProps } from '../../Table';
import { ResizableColumn } from '../types';

export const useResizableColumns = <T>(
  columns: DTableProps<T>['columns'],
  resizable: boolean = true
) => {
  const computeInitialColsState = useCallback(
    (cols: DTableProps<T>['columns']): ResizableColumn<T>[] =>
      (cols ?? []).map((col, _idx) => {
        const { onHeaderCell, width, ...rest } = col;
        return {
          ...rest,
          _idx,
          width: typeof width === 'number' ? width : 180,
        };
      }),
    []
  );

  const [columnsState, setColumnsState] = useState<ResizableColumn<T>[]>(
    computeInitialColsState(columns)
  );

  useEffect(() => {
    const next = computeInitialColsState(columns);
    setColumnsState(prev => {
      // Merge widths from prev to preserve user resizes
      const merged = next.map(col => {
        const prevCol = prev.find(p => p._idx === col._idx);
        return { ...col, width: prevCol?.width ?? col.width };
      });
      return merged;
    });
  }, [columns, computeInitialColsState]);

  const makeHandleResize = useCallback(
    (index: number) =>
      (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
        setColumnsState(prev => {
          if (prev[index]?.width === size.width) return prev;
          const next = [...prev];
          next[index] = { ...next[index], width: size.width };
          return next;
        });
      },
    []
  );

  const mergedColumns: ResizableColumn<T>[] = useMemo(
    () =>
      columnsState.map((col, index) => ({
        ...col,
        onHeaderCell: resizable
          ? () => ({
              width: col.width,
              handleResize: makeHandleResize(index),
            })
          : undefined,
      })),
    [columnsState, resizable, makeHandleResize]
  );

  return { mergedColumns };
};
